import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const CATEGORY_LABEL = {
  alkali_metals: "Alkali Metals",
  alkaline_earth: "Alkaline Earth",
  transition_metals: "Transition Metals",
  post_transition: "Post-Transition",
  metalloids: "Metalloids",
  reactive_nonmetals: "Reactive Non-metals",
  noble_gases: "Noble Gases",
  lanthanides: "Lanthanides",
  actinides: "Actinides",
  superheavy_synthetic: "Superheavy Synthetic",
};

const CATEGORY_RGB = {
  alkali_metals: [239, 68, 68],
  alkaline_earth: [249, 115, 22],
  transition_metals: [250, 204, 21],
  post_transition: [163, 230, 53],
  metalloids: [52, 211, 153],
  reactive_nonmetals: [34, 211, 238],
  noble_gases: [228, 228, 231],
  lanthanides: [96, 165, 250],
  actinides: [129, 140, 248],
  superheavy_synthetic: [39, 39, 42],
};

const HAZARD_LABEL = {
  extreme: "EXTREME",
  high: "HIGH",
  warning: "WARNING",
  safe: "LOW",
  unknown: "UNKNOWN",
};

const HAZARD_RGB = {
  extreme: [255, 0, 60],
  high: [255, 138, 0],
  warning: [255, 215, 0],
  safe: [0, 229, 255],
  unknown: [212, 212, 216],
};

const COMPANY = {
  name: "AnagaHealth",
  address: "803/P, Padmanagar phase-II, Quthbullapur, Hyderabad-500067",
};

/** Render a DOM node into a single-page PDF, sized to fit. */
export async function nodeToPdf(node, filename, { landscape = false } = {}) {
  if (!node) return;
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  const imgData = canvas.toDataURL("image/jpeg", 0.92);
  const orientation = landscape ? "l" : "p";
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const ratio = canvas.width / canvas.height;
  let w = pageW - 40;
  let h = w / ratio;
  if (h > pageH - 40) { h = pageH - 40; w = h * ratio; }
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  pdf.addImage(imgData, "JPEG", x, y, w, h);
  pdf.save(filename);
}

/** Draw a single element "safety sheet" onto an existing pdf at current page. */
function drawElementPage(pdf, el, pageNumber) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  // Header strip
  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, 110, "F");

  // Category swatch
  const catColor = CATEGORY_RGB[el.cat] || [200, 200, 200];
  pdf.setFillColor(...catColor);
  pdf.rect(M, 22, 70, 70, "F");
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(2);
  pdf.rect(M, 22, 70, 70, "S");
  pdf.setTextColor(el.cat === "superheavy_synthetic" || el.cat === "actinides" ? 255 : 0,
                   el.cat === "superheavy_synthetic" || el.cat === "actinides" ? 255 : 0,
                   el.cat === "superheavy_synthetic" || el.cat === "actinides" ? 255 : 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(34);
  pdf.text(el.s, M + 35, 67, { align: "center" });
  pdf.setFontSize(8);
  pdf.text(String(el.n), M + 6, 32);

  // Title block
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(26);
  pdf.text(el.name.toUpperCase(), M + 90, 52);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(180, 180, 180);
  pdf.text(`Atomic № ${el.n}  ·  ${el.mass} u  ·  Period ${el.period}  ·  Group ${el.group}  ·  ${CATEGORY_LABEL[el.cat] || el.cat}`, M + 90, 70);

  // Hazard pill
  const haz = HAZARD_RGB[el.hazard] || [200, 200, 200];
  pdf.setFillColor(...haz);
  pdf.rect(W - M - 110, 32, 110, 32, "F");
  pdf.setTextColor(0, 0, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("HAZARD LEVEL", W - M - 55, 44, { align: "center" });
  pdf.setFontSize(14);
  pdf.text(HAZARD_LABEL[el.hazard] || "—", W - M - 55, 58, { align: "center" });

  // Sections
  let y = 140;
  const sectionH = (label, body, color, dark = false) => {
    pdf.setFillColor(...color);
    pdf.rect(M, y, W - 2 * M, 26, "F");
    pdf.setTextColor(dark ? 255 : 0, dark ? 255 : 0, dark ? 255 : 0);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text(label, M + 10, y + 17);
    y += 32;
    pdf.setTextColor(9, 9, 11);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10.5);
    const lines = pdf.splitTextToSize(body, W - 2 * M - 10);
    pdf.text(lines, M + 5, y + 2);
    y += lines.length * 13 + 14;
  };

  sectionH("HEALTH SYMPTOMS", el.symptoms || "—", [255, 215, 0]);
  sectionH("COMPLICATIONS", el.complications || "—", [255, 138, 0]);
  sectionH("PRECAUTIONS", el.precautions || "—", [0, 229, 255]);
  sectionH("FIRST AID TREATMENT", el.firstaid || "—", [255, 0, 60], true);
  if (el.antidote) {
    sectionH("ANTIDOTES & MEDICINES", el.antidote, [0, 229, 255]);
  }

  // Overview block (description / uses / exposure / limit / discovered)
  if (el.description || el.uses || el.exposure || el.workplace_limit) {
    pdf.setFillColor(244, 244, 245);
    pdf.rect(M, y, W - 2 * M, 22, "F");
    pdf.setTextColor(9, 9, 11);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("OVERVIEW & REFERENCE", M + 10, y + 15);
    y += 26;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    if (el.description) {
      const lines = pdf.splitTextToSize(el.description, W - 2 * M - 10);
      pdf.text(lines, M + 5, y);
      y += lines.length * 12 + 6;
    }
    const kv = (label, val) => {
      if (!val) return;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(80, 80, 80);
      pdf.text(label.toUpperCase(), M + 5, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(9, 9, 11);
      const lines = pdf.splitTextToSize(val, W - 2 * M - 10);
      pdf.text(lines, M + 5, y + 11);
      y += lines.length * 11 + 14;
    };
    kv("Common uses", el.uses);
    kv("Exposure routes", el.exposure);
    kv("Workplace limit", el.workplace_limit);
    kv("Discovered", el.discovered);
  }

  // Isotopes table
  if (el.isotopes && el.isotopes.length > 0) {
    pdf.setFillColor(9, 9, 11);
    pdf.rect(M, y, W - 2 * M, 22, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("KEY ISOTOPES & HALF-LIVES", M + 10, y + 15);
    y += 22;

    const colW = (W - 2 * M) / 4;
    pdf.setDrawColor(9, 9, 11);
    pdf.setLineWidth(0.6);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    ["ISOTOPE", "HALF-LIFE", "DECAY MODE", "ABUNDANCE"].forEach((h, i) => {
      pdf.text(h, M + colW * i + 6, y + 12);
    });
    pdf.line(M, y + 16, W - M, y + 16);
    y += 20;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(9, 9, 11);
    el.isotopes.forEach((iso) => {
      pdf.text(`${el.s}-${iso.a}`, M + 6, y);
      pdf.text(String(iso.half_life || "—"), M + colW + 6, y);
      pdf.text(String(iso.decay || "—"), M + colW * 2 + 6, y);
      pdf.text(String(iso.abundance || "—"), M + colW * 3 + 6, y);
      y += 14;
    });
  }

  // Footer
  pdf.setDrawColor(9, 9, 11);
  pdf.setLineWidth(1);
  pdf.line(M, H - 50, W - M, H - 50);
  pdf.setTextColor(100, 100, 100);
  pdf.setFontSize(8);
  pdf.text(`${COMPANY.name} · CHEM/SAFETY · For laboratory training use only`, M, H - 36);
  pdf.text(COMPANY.address, M, H - 24);
  if (pageNumber) pdf.text(`Page ${pageNumber}`, W - M, H - 36, { align: "right" });
}

/** Cover page (filled solid black). */
function drawCoverPage(pdf, total) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, H, "F");

  // Yellow corner block
  pdf.setFillColor(255, 215, 0);
  pdf.rect(0, 0, 24, H, "F");
  pdf.rect(W - 24, 0, 24, H, "F");

  // Title
  pdf.setTextColor(255, 215, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(54);
  pdf.text("CHEM /", M + 16, H * 0.32);
  pdf.text("SAFETY", M + 16, H * 0.32 + 56);

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(22);
  pdf.text("STUDY BOOK · v1", M + 16, H * 0.32 + 100);

  // Subtitle
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(14);
  pdf.text("All 118 chemical elements", M + 16, H * 0.55);
  pdf.text("Health symptoms · Complications", M + 16, H * 0.55 + 20);
  pdf.text("Precautions · First-aid treatment", M + 16, H * 0.55 + 38);
  pdf.text("Key isotopes & half-lives", M + 16, H * 0.55 + 56);

  // Red emergency stripe
  pdf.setFillColor(255, 0, 60);
  pdf.rect(M, H * 0.75, W - 2 * M, 36, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("FOR LABORATORY TRAINING USE ONLY", M + 12, H * 0.75 + 22);

  // Footer
  pdf.setTextColor(255, 215, 0);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`${total} elements  ·  Generated ${new Date().toLocaleDateString()}`, M + 16, H - 110);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(COMPANY.name, M + 16, H - 86);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(COMPANY.address, M + 16, H - 70);
  pdf.text("chem-safety.app  ·  CHEM/SAFETY", M + 16, H - 54);
}

/** Table of contents page listing all elements with their page numbers. */
function drawTocPage(pdf, elements) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, W, H, "F");

  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, 70, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.text("CONTENTS · 118 ELEMENTS", M, 44);

  pdf.setTextColor(9, 9, 11);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);

  const cols = 3;
  const colW = (W - 2 * M) / cols;
  const rowH = 14;
  const rowsPerCol = Math.ceil(elements.length / cols);
  const startY = 100;

  elements.forEach((el, i) => {
    const col = Math.floor(i / rowsPerCol);
    const row = i % rowsPerCol;
    const x = M + col * colW;
    const y = startY + row * rowH;
    pdf.setFont("helvetica", "bold");
    pdf.text(`${String(el.n).padStart(3, " ")}`, x, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(`${el.s}  ${el.name}`, x + 26, y);
    pdf.setTextColor(120, 120, 120);
    pdf.text(`p.${i + 3}`, x + colW - 22, y, { align: "right" });
    pdf.setTextColor(9, 9, 11);
  });

  pdf.setTextColor(100, 100, 100);
  pdf.setFontSize(8);
  pdf.text(`${COMPANY.name} · CHEM/SAFETY · Study Book · v1`, M, H - 36);
  pdf.text("Page 2", W - M, H - 36, { align: "right" });
}

/** Back cover page (category legend + hazard scale + disclaimer). */
function drawBackPage(pdf, totalPages) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, H, "F");

  pdf.setFillColor(255, 215, 0);
  pdf.rect(0, 0, 24, H, "F");
  pdf.rect(W - 24, 0, 24, H, "F");

  pdf.setTextColor(255, 215, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(32);
  pdf.text("LEGEND & DISCLAIMER", M + 16, 80);

  // Category legend
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.text("Periodic Category Colors", M + 16, 130);

  let y = 150;
  Object.entries(CATEGORY_LABEL).forEach(([key, label]) => {
    const color = CATEGORY_RGB[key];
    pdf.setFillColor(...color);
    pdf.rect(M + 16, y - 10, 18, 14, "F");
    pdf.setDrawColor(255, 255, 255);
    pdf.setLineWidth(0.6);
    pdf.rect(M + 16, y - 10, 18, 14, "S");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(label, M + 42, y);
    y += 20;
  });

  // Hazard scale
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text("Hazard Scale", M + 16, y + 16);
  y += 36;

  Object.entries(HAZARD_LABEL).forEach(([key, label]) => {
    const color = HAZARD_RGB[key];
    pdf.setFillColor(...color);
    pdf.rect(M + 16, y - 10, 18, 14, "F");
    pdf.setDrawColor(255, 255, 255);
    pdf.rect(M + 16, y - 10, 18, 14, "S");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(label, M + 42, y);
    y += 20;
  });

  // Disclaimer block
  pdf.setFillColor(255, 0, 60);
  pdf.rect(M, H * 0.66, W - 2 * M, 110, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("IMPORTANT DISCLAIMER", M + 16, H * 0.66 + 22);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  const disclaimer = pdf.splitTextToSize(
    "This Study Book is a training and reference tool only. In the event of an actual chemical " +
    "exposure, contact your local poison control center and emergency medical services immediately. " +
    "Always consult the manufacturer's Safety Data Sheet (SDS) for the specific chemical in use. " +
    "The publishers are not liable for any decisions made based on this material.",
    W - 2 * M - 32
  );
  pdf.text(disclaimer, M + 16, H * 0.66 + 40);

  pdf.setTextColor(255, 215, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text("CHEM / SAFETY", M + 16, H - 110);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text(`Published by ${COMPANY.name}`, M + 16, H - 92);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(COMPANY.address, M + 16, H - 76);
  pdf.text(`Generated ${new Date().toLocaleDateString()}  ·  ${totalPages} pages  ·  v1`, M + 16, H - 58);
  pdf.text("FOR LABORATORY TRAINING USE ONLY", M + 16, H - 42);
}

/** Build a multi-page Study Book PDF from a list of elements (with embedded isotopes). */
export function buildStudyBook(elements, onProgress) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });

  drawCoverPage(pdf, elements.length);
  pdf.addPage();
  drawTocPage(pdf, elements);

  elements.forEach((el, i) => {
    pdf.addPage();
    if (onProgress) onProgress(i + 1, elements.length);
    drawElementPage(pdf, el, i + 3);
  });

  pdf.addPage();
  drawBackPage(pdf, elements.length + 3);

  pdf.save("chem-safety-study-book.pdf");
}

/** Build a single-element safety sheet PDF. */
export function buildElementSheet(element) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  drawElementPage(pdf, element, null);
  pdf.save(`chem-safety-${element.s}-${element.name.toLowerCase()}.pdf`);
}
